/**
 * EIProfile — single employee profile page.
 */

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowLeft, Loader2, Save, Trash2, FileText, AlertCircle,
  TrendingUp, MessageSquare, Award, Calendar, Briefcase, Building2, UserCircle, ListTodo,
  UserCheck, UserMinus, UserX, HelpCircle,
} from "lucide-react";
import { API, DISC_COLOURS, formatScore, fmtDate, toast } from "../EmploymentIntelligence";
import DiscRadar from "./DiscRadar";
import RetentionFitBadge from "./RetentionFitBadge";

const STATUSES = ["active", "former", "unknown"];

const EMPLOYMENT_STATUSES = [
  { value: "active",       label: "Active",       Icon: UserCheck,  color: "#10b981" },
  { value: "good_leaver",  label: "Good leaver",  Icon: UserMinus,  color: "#0ea5e9" },
  { value: "bad_leaver",   label: "Bad leaver",   Icon: UserX,      color: "#e11d48" },
  { value: "unknown",      label: "Unknown",      Icon: HelpCircle, color: "#6a707d" },
];

export default function EIProfile({ profileId, onBack, onRefresh }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!profileId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/employment-intelligence/profiles/${profileId}`);
        if (cancelled) return;
        setData(res.data);
        setDraft({
          full_name: res.data.profile.full_name,
          job_title: res.data.profile.job_title || "",
          department: res.data.profile.department || "",
          manager: res.data.profile.manager || "",
          status: res.data.profile.status,
          start_date: res.data.profile.start_date || "",
          end_date: res.data.profile.end_date || "",
          tags: res.data.profile.tags || [],
        });
      } catch {
        if (!cancelled) toast.error("Could not load profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [profileId]);

  const handleEmploymentStatus = async (value) => {
    try {
      const res = await axios.patch(
        `${API}/employment-intelligence/profiles/${profileId}/employment-status`,
        { employment_status: value },
      );
      setData(res.data);
      onRefresh?.();
      toast.success(`${data?.profile?.full_name || "Profile"} → ${value.replace("_", " ")}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const latest = useMemo(() => {
    if (!data?.assessments?.length) return null;
    return data.assessments[0];
  }, [data]);

  if (!profileId) return null;
  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#6a707d" }}>
        <Loader2 size={22} className="ei-spin" />
      </div>
    );
  }
  if (!data) return null;

  const disc = latest?.disc || {};
  const ba = latest?.behavioural_analysis || {};

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/employment-intelligence/profiles/${profileId}`, draft);
      setData(res.data);
      onRefresh?.();
      toast.success("Profile saved");
    } catch {
      toast.error("Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${data.profile.full_name} and all their assessments? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/employment-intelligence/profiles/${profileId}`);
      toast.success("Profile deleted");
      onRefresh?.();
      onBack();
    } catch {
      toast.error("Could not delete profile");
    }
  };

  return (
    <>
      <div className="ei-section-head">
        <div>
          <button className="ei-secondary" onClick={onBack} style={{ marginBottom: 8 }}>
            <ArrowLeft size={13} /> Back to directory
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <h2 style={{ margin: 0 }}>{data.profile.full_name}</h2>
            {data.profile.retention_fit && (
              <RetentionFitBadge fit={data.profile.retention_fit} size="lg" />
            )}
          </div>
          <p>
            {data.profile.job_title || "Job title not set"} · {data.profile.department || "No department"}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="ei-secondary" onClick={handleDelete} data-testid="ei-profile-delete">
            <Trash2 size={13} /> Delete
          </button>
          <button className="ei-primary" onClick={handleSave} disabled={saving} data-testid="ei-profile-save">
            {saving ? <Loader2 size={13} className="ei-spin" /> : <Save size={13} />} Save changes
          </button>
        </div>
      </div>

      <div className="ei-profile-grid">
        {/* Left column: editable fields + DISC radar + scores */}
        <div className="ei-profile-summary">
          <div className="ei-profile-card">
            <h3>Profile details</h3>
            <Field label="Full name" value={draft.full_name} onChange={(v) => setDraft({ ...draft, full_name: v })} />
            <Field label="Job title" value={draft.job_title} onChange={(v) => setDraft({ ...draft, job_title: v })} />
            <Field label="Department" value={draft.department} onChange={(v) => setDraft({ ...draft, department: v })} />
            <Field label="Manager" value={draft.manager} onChange={(v) => setDraft({ ...draft, manager: v })} />

            {/* Employment status switcher */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
              <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "#6a707d" }}>
                Employment status
              </span>
              <div className="ei-employment-switcher" data-testid="ei-employment-switcher">
                {EMPLOYMENT_STATUSES.map(({ value, label, Icon, color }) => {
                  const on = data.profile.employment_status === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleEmploymentStatus(value)}
                      className={`ei-employment-btn ${on ? "is-on" : ""}`}
                      style={{ "--accent": color }}
                      data-testid={`ei-employment-${value}`}
                    >
                      <Icon size={13} /> {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <FieldSelect
              label="Tags"
              value={null}
              multi
              tags={draft.tags}
              options={["High Performer", "Promotion", "Management", "Top Talent", "Underperformer"]}
              onChange={(tags) => setDraft({ ...draft, tags })}
            />
          </div>

          {latest && (
            <div className="ei-profile-card">
              <h3>DISC profile</h3>
              <div style={{ display: "flex", justifyContent: "center", padding: "4px 0 8px" }}>
                <div style={{ width: "min(280px, 100%)" }}>
                  <DiscRadar scores={disc} />
                </div>
              </div>
              <div className="ei-score-grid">
                {["D", "I", "S", "C"].map((letter) => {
                  const num = disc[{ D: "dominance", I: "influence", S: "steadiness", C: "compliance" }[letter]];
                  return (
                    <div key={letter} className="ei-score-tile">
                      <div className="ei-score-tile-letter" style={{ background: DISC_COLOURS[letter].fill }}>{letter}</div>
                      <div className="ei-score-tile-num">{formatScore(num)}</div>
                      <div className="ei-score-tile-label">{DISC_COLOURS[letter].label}</div>
                    </div>
                  );
                })}
              </div>
              <div className="ei-profile-meta" style={{ marginTop: 14 }}>
                {disc.blend && (
                  <div><strong>Blend:</strong> {disc.blend}</div>
                )}
                {latest.date_assessed && (
                  <div><strong>Assessed:</strong> {latest.date_assessed}</div>
                )}
                <div>
                  <strong>AI confidence:</strong>{" "}
                  <span className="ei-ai-confidence">{Math.round((latest.ai_confidence || 0) * 100)}%</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right column: AI behavioural analysis */}
        <div className="ei-profile-summary">
          {!latest ? (
            <div className="ei-profile-card">
              <p>No assessment uploaded for this profile yet.</p>
            </div>
          ) : (
            <>
              {ba.personality_summary && (
                <AnalysisCard Icon={UserCircle} title="Personality summary">
                  <p>{ba.personality_summary}</p>
                </AnalysisCard>
              )}
              {ba.communication_style && (
                <AnalysisCard Icon={MessageSquare} title="Communication style">
                  <p>{ba.communication_style}</p>
                </AnalysisCard>
              )}
              {ba.leadership_style && (
                <AnalysisCard Icon={Award} title="Leadership style">
                  <p>{ba.leadership_style}</p>
                </AnalysisCard>
              )}
              {(ba.motivators?.length > 0 || ba.stress_triggers?.length > 0) && (
                <div className="ei-grid-2">
                  {ba.motivators?.length > 0 && (
                    <AnalysisCard Icon={TrendingUp} title="Motivators">
                      <ul className="ei-trait-list">{ba.motivators.map((t, i) => <li key={i} className="ei-trait">{t}</li>)}</ul>
                    </AnalysisCard>
                  )}
                  {ba.stress_triggers?.length > 0 && (
                    <AnalysisCard Icon={AlertCircle} title="Stress triggers">
                      <ul className="ei-trait-list">{ba.stress_triggers.map((t, i) => <li key={i} className="ei-trait">{t}</li>)}</ul>
                    </AnalysisCard>
                  )}
                </div>
              )}
              {(ba.strengths?.length > 0 || ba.development_areas?.length > 0) && (
                <div className="ei-grid-2">
                  {ba.strengths?.length > 0 && (
                    <AnalysisCard Icon={Award} title="Strengths">
                      <ul className="ei-trait-list">{ba.strengths.map((t, i) => <li key={i} className="ei-trait">{t}</li>)}</ul>
                    </AnalysisCard>
                  )}
                  {ba.development_areas?.length > 0 && (
                    <AnalysisCard Icon={ListTodo} title="Development areas">
                      <ul className="ei-trait-list">{ba.development_areas.map((t, i) => <li key={i} className="ei-trait">{t}</li>)}</ul>
                    </AnalysisCard>
                  )}
                </div>
              )}
              {ba.ideal_environment && (
                <AnalysisCard Icon={Briefcase} title="Ideal working environment">
                  <p>{ba.ideal_environment}</p>
                </AnalysisCard>
              )}

              <div className="ei-profile-card">
                <h3>Assessment history</h3>
                {data.assessments.map((a) => (
                  <div
                    key={a.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 0",
                      borderBottom: "1px solid #f1f2ec",
                      fontSize: 13,
                    }}
                  >
                    <div>
                      <strong style={{ fontSize: 13 }}>DISC</strong>{" "}
                      <span style={{ color: "#6a707d", marginLeft: 8 }}>
                        {a.date_assessed || fmtDate(a.created_at)}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span className="ei-ai-confidence">{Math.round((a.ai_confidence || 0) * 100)}%</span>
                      {a.pdf_id && (
                        <a
                          href={`${API}/employment-intelligence/pdfs`}
                          className="ei-link-btn"
                          onClick={(e) => e.preventDefault()}
                          title="Original PDF stored on the platform"
                        >
                          <FileText size={13} /> PDF
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function AnalysisCard({ Icon, title, children }) {
  return (
    <div className="ei-profile-card">
      <h3 style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <Icon size={13} /> {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
      <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "#6a707d" }}>
        {label}
      </span>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        style={{
          border: "1px solid #d8dcd4",
          borderRadius: 8,
          padding: "9px 12px",
          fontSize: 13.5,
          fontFamily: "inherit",
          color: "#1a1d22",
        }}
      />
    </div>
  );
}

function FieldSelect({ label, value, options, onChange, multi, tags }) {
  if (!multi) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
        <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "#6a707d" }}>
          {label}
        </span>
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="ei-select"
          style={{ minWidth: 0 }}
        >
          {options.map((o) => <option key={o} value={o}>{o[0].toUpperCase() + o.slice(1)}</option>)}
        </select>
      </div>
    );
  }
  const toggleTag = (t) => {
    if (tags.includes(t)) onChange(tags.filter((x) => x !== t));
    else onChange([...tags, t]);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 4 }}>
      <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "#6a707d" }}>
        {label}
      </span>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {options.map((t) => {
          const on = tags.includes(t);
          return (
            <button
              key={t}
              type="button"
              onClick={() => toggleTag(t)}
              style={{
                fontFamily: "inherit",
                fontSize: 11,
                fontWeight: 700,
                padding: "3px 10px",
                borderRadius: 999,
                border: `1px solid ${on ? "#1a472a" : "#d8dcd4"}`,
                background: on ? "rgba(26, 71, 42, 0.1)" : "#fff",
                color: on ? "#1a472a" : "#6a707d",
                cursor: "pointer",
              }}
            >
              {t}
            </button>
          );
        })}
      </div>
    </div>
  );
}
